<!-- eslint-disable -->
<template>
  <div ref="videoCallContainer" :class="{'modal-body': true, 'rounded': isInModal && !isCallInFullscreen, 'paints-stage': paintsStage}">
    <div class="call-main">
    <DevicePermissionBanner
      v-if="shouldDisplayDevicePermissionPrompt"
      :headline="devicePromptHeadline"
      :message="devicePromptMessage"
      :denied-device-labels="deniedDeviceDisplayLabels"
      :is-requesting-device-permission="isRequestingDevicePermission"
      :is-auto-join-in-progress="isAutoJoinInProgress"
      :show-retry-join="Boolean(autoJoinErrorMessage) && !isAutoJoinRefused"
      :show-retry-access="!isAutoJoinRefused"
      @retry-access="requestDeviceAccessForCall"
      @retry-join="retryAutoJoinRequest"
    />

    <iRow
      width="fill"
      height="fillv2"
      gap="none"
      wrap="nowrap"
      vertical-align="stretch"
      :min-height="0"
      data-test="call-stage-row"
    >
    <div class="video-stage" :class="[`layout-${stageLayoutMode}`, {'paints-stage': paintsStage}]">
      <PreJoinLobby
        v-if="showPreJoinLobby"
        :camera-tile="prejoinCameraTile"
        :is-local-video-mirrored="isLocalVideoMirrored"
        :available-video-devices="availableVideoDevices"
        :available-audio-input-devices="availableAudioInputDevices"
        :available-output-devices="availableOutputDevices"
        :selected-camera-device-id="selectedCameraDeviceId"
        :selected-microphone-device-id="selectedMicrophoneDeviceId"
        :selected-output-device-id="selectedOutputDeviceId"
        :mic-level="micLevel"
        :is-microphone-enabled="prejoinMicrophoneState"
        :is-camera-enabled="prejoinCameraState"
        :is-joining="isLivekitConnecting || isAutoJoinInProgress || callPhase === 'joining'"
        :display-name="localDisplayName"
        :avatar-url="userPhotoUrl"
        :event-title="callTitle"
        :is-broadcast="isBroadcasting"
        :participant-count="participantCount"
        :notice="lobbyNoticeText"
        :join-label="lobbyJoinLabel"
        :title="lobbyTitle"
        :is-guest="isGuest"
        :guest-name="guestName"
        @update:guest-name="guestName = $event"
        @sign-in="signInInstead"
        @close="$emit('close')"
        @toggle-mic="handlePreJoinToggleMic"
        @toggle-camera="handlePreJoinToggleCamera"
        @change-camera="handlePreJoinCameraChange"
        @change-microphone="handlePreJoinMicrophoneChange"
        @change-output="commitOutputDeviceSelection($event)"
        @open-settings="toggleSettingsPanel"
        @join="joinCallWithPrejoinState()"
        @join-audio-only="joinCallWithPrejoinState({ audioOnly: true })"
      />

      <iColumn
        v-else-if="isWaitingForHost"
        width="fill"
        height="fill"
        gap="standard"
        align="center"
        vertical-align="middle"
        wrap="nowrap"
        data-test="waiting-room"
      >
        <CallEmptyState
          :headline="emptyStateHeadline"
          :message="emptyStateSupportingMessage"
          :is-joining-or-waiting="true"
          :display-name="localDisplayName"
        />
        <iButton variant="text" size="small" data-test="waiting-room-leave" @click="stopWaiting">
          Stop waiting
        </iButton>
      </iColumn>

      <CallEmptyState
        v-else-if="isJoiningOrWaiting"
        :headline="emptyStateHeadline"
        :message="emptyStateSupportingMessage"
        :is-joining-or-waiting="isJoiningOrWaiting"
        :display-name="localDisplayName"
        :can-cancel="canCloseManually"
        @cancel="handleLeaveRequest"
      />

      <CallStage
        v-else-if="isInCall"
        :tiles="stageTiles"
        :layout-mode="stageLayoutMode"
        :primary-peer-id="primaryViewPeerId"
        :focused-peer-id="focusedPeerId"
        :max-tiles="viewPreferences.maxTiles"
        :hide-tiles-without-video="viewPreferences.hideTilesWithoutVideo"
        :settle-delta="stageSettleDelta"
        :is-participant-speaking="isParticipantSpeaking"
        :is-local-video-mirrored="isLocalVideoMirrored"
        :is-camera-enabled="isCameraEnabled"
        :is-microphone-enabled="isMicrophoneEnabled"
        :selected-output-device-id="selectedOutputDeviceId"
        @focus-tile="focusGridTileInSpeakerView"
        @restore-speaker-view="restorePreviousSpeakerView"
        @subscription-plan="applyVideoSubscriptionPlan"
        @stop-presenting="stopPresenting"
        @show-participants="showParticipants"
      />
      <ReactionBurstLayer v-if="isInCall" :bursts="reactionBursts" />

      <iRow
        v-if="showCallControlBar"
        gap="small"
        width="hug"
        vertical-align="middle"
        wrap="nowrap"
        position="absolute"
        :position-placements="{top: '0.75rem', bottom: 'unset', left: '0.75rem', right: 'unset'}"
        :z-index="60"
      >
        <iRow
          v-if="!isBroadcasting && showTitlePill && !titleSitsInControlBar"
          gap="small"
          width="hug"
          vertical-align="middle"
          wrap="nowrap"
          data-test="call-title-pill"
          :style-overrides="{
            backgroundColor: 'subtle',
            borderRadius: 'standard',
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            paddingLeft: 'small',
            paddingRight: 'small',
          }"
        >
          <CallElapsedTimer v-if="roomAppearance.showElapsedTimer" :joined-at-ms="livekitConnection?.joinedAtMs || 0" />
          <iText
            v-if="roomAppearance.showEventTitle"
            font-size="small"
            ellipsis
            :max-width="320"
            data-test="call-title-text"
          >
            {{ videoCallTitle }}
          </iText>
        </iRow>
        <iRow
          v-else-if="isBroadcasting && showBroadcastPill"
          gap="small"
          width="hug"
          vertical-align="middle"
          wrap="nowrap"
          data-test="call-broadcast-pill"
          :style-overrides="{
            backgroundColor: 'subtle',
            borderRadius: 'standard',
            paddingTop: 'extraSmall',
            paddingBottom: 'extraSmall',
            paddingLeft: 'small',
            paddingRight: 'small',
          }"
        >
          <iBadge
            v-if="isInCall && roomAppearance.showLiveBadge"
            label="LIVE"
            variant="striking"
            size="small"
            pulse
            data-test="call-live-chip"
          />
          <CallElapsedTimer v-if="roomAppearance.showElapsedTimer" :joined-at-ms="livekitConnection?.joinedAtMs || 0" />
          <iText v-if="roomAppearance.showViewerCount" font-size="small" data-test="call-viewer-count">
            {{ audienceSummary }}
          </iText>
        </iRow>
      </iRow>

      <iColumn
        v-if="(showStageHint || showKnockNotice) && !sidePanel && !showCameraPausedNotice && !showAudioPlaybackBlockedNotice && !isWindowedCall"
        width="fill"
        align="center"
        position="absolute"
        :position-placements="{top: showTopLeftPill ? '3.25rem' : '0.75rem', bottom: 'unset', left: '0', right: '0'}"
        :z-index="55"
      >
        <iCallNotice
          v-if="showKnockNotice"
          icon="ic:outline-person-add"
          action-label="View"
          action-test-id="call-knock-notice-view"
          data-test="call-knock-notice"
          @action="setRosterPanelVisible(true)"
        >
          {{ knockNoticeText }}
        </iCallNotice>
        <iCallNotice
          v-else
          :icon="isBroadcasting ? 'ic:outline-visibility' : 'ic:outline-group'"
          :action-label="inviteLink ? (isInviteLinkCopied ? 'Link copied' : 'Copy invite link') : ''"
          action-test-id="call-alone-copy-link"
          :data-test="isBroadcasting ? 'call-no-audience-hint' : 'call-alone-hint'"
          @action="copyInviteLinkFromNotice"
        >
          {{ stageHintText }}
        </iCallNotice>
      </iColumn>

      <iColumn
        v-if="showAudioPlaybackBlockedNotice"
        width="fill"
        align="center"
        position="absolute"
        :position-placements="{top: '3.5rem', bottom: 'unset', left: '0', right: '0'}"
        :z-index="61"
        :style-overrides="{ paddingLeft: 'standard', paddingRight: 'standard' }"
      >
        <AudioPlaybackBlockedNotice @resume="resumeAudioPlayback" />
      </iColumn>

      <iColumn
        v-if="showMicrophoneSilentNotice"
        width="fill"
        align="center"
        position="absolute"
        :position-placements="{top: '3.5rem', bottom: 'unset', left: '0', right: '0'}"
        :z-index="61"
        :style-overrides="{ paddingLeft: 'standard', paddingRight: 'standard' }"
      >
        <iCallNotice icon="ic:outline-mic-off" data-test="call-microphone-silent-notice">
          {{ microphoneSilentNotice }}
        </iCallNotice>
      </iColumn>

      <iColumn
        v-if="showCameraPausedNotice"
        width="fill"
        align="center"
        position="absolute"
        :position-placements="{top: '3.5rem', bottom: 'unset', left: '0', right: '0'}"
        :z-index="60"
        :style-overrides="{ paddingLeft: 'standard', paddingRight: 'standard' }"
      >
        <CameraPausedNotice
          @turn-camera-on="keepCameraOnDespiteWeakUplink"
          @dismiss="isCameraPausedNoticeDismissed = true"
        />
      </iColumn>

      <iColumn
        v-if="callStateBannerVisible"
        width="fill"
        align="center"
        position="absolute"
        :position-placements="{top: '3.5rem', bottom: 'unset', left: '0', right: '0'}"
        :z-index="60"
      >
        <CallStateBanner
          :phase="callPhase"
          :errorMessage="callStateErrorMessage"
          @retry="retryAutoJoinRequest"
        />
      </iColumn>
    </div>
    <iPanelReveal
      ref="sideReveal"
      :open="Boolean(sidePanel)"
      :width="sidePanelWidth"
      :mode="renderedSidePanel === 'chat' ? chatPanelMode : peoplePanelMode"
      data-test="call-side-reveal"
      @closed="renderedSidePanel = null"
    >
      <template #default="{ isClosing }">
        <iColumn
          :width="sidePanelWidth"
          height="fill"
          gap="none"
          wrap="nowrap"
          :min-height="0"
          data-test="call-side-frame"
          :style-overrides="{paddingTop: 'small', paddingBottom: 'small', paddingRight: 'small'}"
        >
        <CallSidePanel
          v-if="renderedSidePanel === 'people'"
          panel="people"
          :title="isBroadcasting ? 'Viewers' : 'People'"
          :content-hidden="isClosing"
          close-test-id="call-roster-close"
          @close="closeSidePanels"
        >
          <CallRosterPanel
            :entries="rosterEntriesForPanel"
            :canModerate="canModerateRoom"
            :speakingPeerIds="speakingPeerIds"
            :isBroadcast="isBroadcasting"
            :roomState="eventRoomState"
            :inviteLink="inviteLink"
            @lower-hand="lowerHand"
            @mute-participant="muteParticipant"
            @request-unmute="requestUnmute"
            @remove-participant="askToRemove"
            @update-room-state="updateRoomState"
            @admit-user="admitUser"
            @deny-user="denyUser"
            @admit-guest="admitGuest"
            @deny-guest="denyGuest"
            @end-for-everyone="endForEveryone"
          />
        </CallSidePanel>
        <slot v-else-if="renderedSidePanel === 'chat'" name="chat-panel" :is-closing="isClosing" />
        </iColumn>
      </template>
    </iPanelReveal>
    </iRow>
    <div class="audio-host" aria-hidden="true">
      <AudioTrackRenderer
          v-for="tile in audioHostTiles"
          :key="tile.id"
          :audio-track="tile.audioTrack"
          :sink-id="selectedOutputDeviceId"
      />
    </div>
    <CallControlBar
      v-if="showCallControlBar"
      :layout="controlBarLayout"
      :isInCall="isInCall"
      :isRosterOpen="sidePanel === 'people'"
      :isSettingsOpen="isSettingsPanelVisible"
      :isCallInFullscreen="isCallInFullscreen"
      :canCloseManually="canCloseManually"
      :isInModal="isInModal"
      :showPopControls="showPopControls"
      :isMicrophoneEnabled="isMicrophoneEnabled"
      :isCameraEnabled="isCameraEnabled"
      :isScreenShareActive="isScreenShareActive"
      @toggle-mic="toggleMicrophoneAndUpdatePermissions"
      @toggle-camera="toggleCameraAndUpdatePermissions"
      @toggle-screenshare="handleScreenShareButtonClick"
      @open-adjust-view="setAdjustViewPanelVisible(true)"
      @pop-in="emitPopInRequest"
      @pop-out="emitPopOutRequest"
      @enter-fullscreen="enterCallFullscreen"
      @exit-fullscreen="exitCallFullscreen"
      :can-adjust-view="!isBroadcasting"
      :roster-label="isBroadcasting ? 'Show viewers' : 'Show participants'"
      :show-break="isBroadcasting && canModerateRoom && Boolean(eventId)"
      :leave-label="isBroadcasting ? 'End stream' : 'Leave'"
      :leave-description="isBroadcasting ? 'End the stream for everyone' : 'Leave the call'"
      :show-leave-label="isBroadcasting"
      @leave="handleLeaveRequest"
      @break="pauseBroadcast"
      :is-hand-raised="isHandRaised"
      :can-react="canPublishData"
      @toggle-hand="toggleHand"
      @send-reaction="sendReaction"
      :keep-bar-visible="isRosterPanelVisible || isSettingsPanelVisible || isAdjustViewPanelVisible"
      @toggle-roster="toggleRosterPanel"
      @toggle-settings="toggleSettingsPanel"
      :can-open-chat="!isGuest"
      :has-chat="hostHasChat"
      :is-chat-open="sidePanel === 'chat'"
      :is-local-speaking="isParticipantSpeaking(localSpeakerId)"
      :participant-count="rosterEntriesForPanel.length"
      :available-audio-input-devices="availableAudioInputDevices"
      :available-output-devices="availableOutputDevices"
      :available-video-devices="availableVideoDevices"
      :selected-microphone-device-id="selectedMicrophoneDeviceId"
      :selected-output-device-id="selectedOutputDeviceId"
      :selected-camera-device-id="selectedCameraDeviceId"
      @toggle-chat="toggleChatSidebar"
      @change-microphone="applySelectedMicrophoneDevice($event)"
      @change-output="commitOutputDeviceSelection($event)"
      @change-camera="applySelectedCameraDevice($event)"
    >
      <template v-if="titleSitsInControlBar && showTitlePill" #leading>
        <iRow
          gap="small"
          width="hug"
          vertical-align="middle"
          wrap="nowrap"
          :min-width="0"
          data-test="call-title-pill"
        >
          <CallElapsedTimer v-if="roomAppearance.showElapsedTimer" :joined-at-ms="livekitConnection?.joinedAtMs || 0" />
          <iText
            v-if="roomAppearance.showElapsedTimer && roomAppearance.showEventTitle"
            variant="subtle"
            :wrap="false"
          >
            |
          </iText>
          <iText
            v-if="roomAppearance.showEventTitle"
            font-weight="dark"
            :wrap="false"
            :min-width="0"
            :tooltip="videoCallTitle"
            ellipsis
            data-test="call-title-text"
          >
            {{ videoCallTitle }}
          </iText>
        </iRow>
      </template>
    </CallControlBar>
    </div>

    <iModal
      v-if="isEndStreamConfirmVisible"
      title="End the stream?"
      primary-action-button-label="End stream"
      secondary-action-button-label="Keep streaming"
      :max-width="440"
      data-test="end-stream-confirm"
      @close="isEndStreamConfirmVisible = false"
      @click:secondary="isEndStreamConfirmVisible = false"
      @click:primary="confirmEndStream"
    >
      <iText>
        This ends the stream for everyone watching. The event is marked as ended and can't be restarted.
      </iText>
    </iModal>

    <iModal
      v-if="hasUnmuteRequest"
      title="The host asked you to unmute"
      primary-action-button-label="Unmute"
      secondary-action-button-label="Not now"
      :max-width="440"
      data-test="unmute-request"
      @close="dismissUnmuteRequest"
      @click:secondary="dismissUnmuteRequest"
      @click:primary="acceptUnmuteRequest"
    >
      <iText>
        Your microphone stays off until you turn it on.
      </iText>
    </iModal>

    <iModal
      v-if="pendingRemoval"
      :title="`Remove ${pendingRemoval.displayName} from the event?`"
      primary-action-button-label="Remove"
      secondary-action-button-label="Keep them"
      :max-width="440"
      data-test="remove-participant-confirm"
      @close="pendingRemoval = null"
      @click:secondary="pendingRemoval = null"
      @click:primary="confirmRemoveParticipant"
    >
      <iText>
        They are disconnected right away and can't rejoin for the rest of this event.
      </iText>
    </iModal>

    <AdjustViewPanel
      v-if="showCallControlBar"
      :open="isAdjustViewPanelVisible"
      :preferences="viewPreferences"
      @change="applyViewPreferences($event)"
      @close="setAdjustViewPanelVisible(false)"
    />

    <CallSettingsPanel
      v-if="showCallControlBar || showPreJoinLobby"
      :open="isSettingsPanelVisible"
      :availableVideoDevices="availableVideoDevices"
      :availableAudioInputDevices="availableAudioInputDevices"
      :availableOutputDevices="availableOutputDevices"
      :selectedCameraDeviceId="selectedCameraDeviceId"
      :selectedMicrophoneDeviceId="selectedMicrophoneDeviceId"
      :selectedOutputDeviceId="selectedOutputDeviceId"
      :isLocalVideoMirrored="isLocalVideoMirrored"
      :noiseCancellationMode="noiseCancellationMode"
      :audioProcessing="audioProcessing"
      :micTest="micTest"
      @record-mic-test="recordMicTestSample({ fallbackTrack: prejoinAudioTrack })"
      @play-mic-test="playMicTestSample()"
      @close="setSettingsPanelVisible(false)"
      @change-camera="applySelectedCameraDevice($event)"
      @change-microphone="applySelectedMicrophoneDevice($event)"
      @change-output="commitOutputDeviceSelection($event)"
      @toggle-mirror="toggleLocalMirroringPreference($event)"
      @change-noise-cancellation="setNoiseCancellationMode($event)"
    />

  </div>
</template>

<script>
import { callRejoinMarkers } from "%/lib/browser-state-stores";
import { mapActions, mapState, mapWritableState } from "pinia";
import { useLivekitStore } from "%/apps/video-engine/stores/livekit-store";
import { useVideoCallStore } from "%/apps/video-engine/stores/video-call-store";
import { useVideoCallUiStore } from "%/apps/video-engine/stores/video-call-ui-store";
import { useParticipantTilesStore } from "%/apps/video-engine/stores/participant-tiles-store";
import { pickValidDeviceId } from "%/apps/video-engine/device-catalog";
import { MICROPHONE_SILENT_NOTICE } from "%/apps/video-engine/audio/microphone-source-watch";
import {
  ADMISSION_ADMITTED,
  ADMISSION_LOCKED,
  ADMISSION_WAITING,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_LOCKED,
  WAITING_ROOM_POLL_MS,
} from "%/apps/video-engine/constants";
import { buildEventInviteLink } from "%/apps/video-engine/event-invite-link";
import { pickPrimaryPeerId } from "%/apps/video-engine/primary-peer";
import { resolveStageLayout } from "%/apps/video-engine/stage-layout";
import { CALL_SHORTCUTS, isTypingTarget, matchCallShortcut } from "%/apps/video-engine/call-shortcuts";
import { createMicLevelMeter } from "%/apps/video-engine/audio/mic-level-meter";

const MIC_METER_ATTACH_DELAY_MS = 2000;
import { useGeneralStore } from "%/stores/general-store";
import DevicePermissionBanner from "%/apps/video-engine/components/video-call/DevicePermissionBanner.vue";
import CallEmptyState from "%/apps/video-engine/components/video-call/CallEmptyState.vue";
import CallStage from "%/apps/video-engine/components/video-call/stage/CallStage.vue";
import ReactionBurstLayer from "%/apps/video-engine/components/video-call/stage/ReactionBurstLayer.vue";
import CallControlBar from "%/apps/video-engine/components/video-call/controls/CallControlBar.vue";
import CallElapsedTimer from "%/apps/video-engine/components/video-call/controls/CallElapsedTimer.vue";
import CallSettingsPanel from "%/apps/video-engine/components/video-call/settings/CallSettingsPanel.vue";
import AdjustViewPanel from "%/apps/video-engine/components/video-call/settings/AdjustViewPanel.vue";
import CallRosterPanel from "%/apps/video-engine/components/video-call/roster/CallRosterPanel.vue";
import CallSidePanel from "%/apps/video-engine/components/video-call/panels/CallSidePanel.vue";
import CallStateBanner from "%/apps/video-engine/components/video-call/CallStateBanner.vue";
import CameraPausedNotice from "%/apps/video-engine/components/video-call/CameraPausedNotice.vue";
import AudioPlaybackBlockedNotice from "%/apps/video-engine/components/video-call/AudioPlaybackBlockedNotice.vue";
import { rememberInCall, forgetInCall, readRejoin } from "%/apps/video-engine/rejoin-marker";
import { signInUrlForEvent } from "%/apps/video-engine/event-room-join";
import PreJoinLobby from "%/apps/video-engine/components/video-call/PreJoinLobby.vue";
import { createPermissionTracker, inferDeniedDevicesFromError as inferDeniedDevicesFromErrorUtil } from "%/apps/video-engine/engine/permissions-service";
import { enterFullscreen, exitFullscreen } from "%/apps/video-engine/engine/fullscreen-service";
import { LOCAL_CAMERA_ID, LOCAL_SHARE_ID } from "%/apps/video-engine/constants";
import { isViewerParticipant } from "%/apps/video-engine/participant-roles";
import AudioTrackRenderer from "%/apps/video-engine/components/video-call/shared/AudioTrackRenderer.vue";
import { planPreviewTracks } from "%/apps/video-engine/engine/preview-tracks";
import { RoomAppearanceMixin } from "%/apps/video-engine/room-appearance";
import { assertCallRoom } from "%/apps/video-engine/call-room";
import { isJoinRefusal, refusalBanner } from "%/apps/video-engine/join-refusal";
import { CONTROL_BAR_LAYOUTS, CONTROL_BAR_LAYOUT_ADAPTIVE, CONTROL_BAR_LAYOUT_STANDARD } from "%/apps/video-engine/control-bar-fit";

const SIDE_PANEL_WIDTH = 368;
const STAGE_SETTLE_MS = 360;
const INVITE_COPIED_MS = 2400;

export default {
  name: "VideoCallRoot",
  components: {
    AudioTrackRenderer,
    DevicePermissionBanner,
    CallEmptyState,
    CallStage,
    ReactionBurstLayer,
    CallControlBar,
    CallElapsedTimer,
    CallSettingsPanel,
    AdjustViewPanel,
    CallRosterPanel,
    CallSidePanel,
    CallStateBanner,
    CameraPausedNotice,
    AudioPlaybackBlockedNotice,
    PreJoinLobby,
  },
  mixins: [RoomAppearanceMixin],
  props: {
    isChatOpen: {
      type: Boolean,
      required: false,
      default: false,
    },
    chatPanelMode: {
      type: String,
      required: false,
      default: "push",
      validator: mode => ["push", "overlay"].includes(mode),
    },
    callRoom: {
      type: Object,
      required: false,
      default: null,
      validator: callRoom => callRoom === null || Boolean(assertCallRoom(callRoom)),
    },
    controlBarLayout: {
      type: String,
      required: false,
      default: CONTROL_BAR_LAYOUT_STANDARD,
      validator: layout => CONTROL_BAR_LAYOUTS.includes(layout),
    },
    canCloseManually: {
      type: Boolean,
      default: true,
    },
    eventId: {
      type: Number,
      required: false,
      default: 0,
    },
    canModerateRoom: {
      type: Boolean,
      required: false,
      default: false,
    },
    isBroadcasting: {
      type: Boolean,
      required: false,
      default: false,
    },
    showCall: {
      type: Boolean,
      required: true,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
    showPopControls: {
      type: Boolean,
      default: true,
    },
    callTitle: {
      type: String,
      required: false,
      default: "",
    },
    participantCount: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ["close", "pop-out", "pop-in", "toggle-chat"],
  data() {
    return {
      sidePanel: null,
      renderedSidePanel: null,
      isSwappingToChat: false,
      stageSettleDelta: null,
      isInviteLinkCopied: false,
      guestName: "",
      permissionTracker: null,
      isCameraPausedNoticeDismissed: false,
      isPushToTalkActive: false,
      prejoinMicrophoneEnabled: true,
      prejoinCameraEnabled: true,
      prejoinPreviewStream: null,
      isLeavingIntentionally: false,
      previewRequestToken: 0,
      hasUnmounted: false,
      isEndStreamConfirmVisible: false,
      isOnBreak: false,
      lobbyNotice: "",
      pendingRemoval: null,
      waitingPollTimer: null,
      rosterPollTimer: null,
      micMeterStartTimer: null,
      lastJoinMediaOptions: null,
      micLevel: 0,
      primaryChosenByUser: false,
    };
  },
  computed: {
    ...mapState(useLivekitStore, {
      connecting: "isConnecting",
      connected: "isConnected",
      disconnectReason: state => state.connection.disconnectReason,
      livekitRoom: state => state.connection.room,
      livekitConnection: state => state.connection,
      remoteParticipantSummaries: "remoteParticipants",
      audienceCount: "viewerCount",
      micOn: state => state.local.micEnabled,
      cameraOn: state => state.local.cameraEnabled,
      cameraPausedForAudio: state => state.local.cameraPausedForAudio,
      sharing: "isScreenSharing",
      includeScreenAudio: state => state.local.includeScreenAudio,
      videoInputs: state => state.devices.videoInputs || [],
      audioInputs: state => state.devices.audioInputs || [],
      audioOutputs: state => state.devices.audioOutputs || [],
      preferredMic: "preferredMic",
      preferredCamera: "preferredCamera",
      audioOnlyPreference: state => state.preferences.audioOnly,
      noiseCancellationMode: "noiseCancellationMode",
      audioProcessing: "audioProcessing",
      micTest: "micTest",
      audioPlaybackBlocked: state => state.connection.audioPlaybackBlocked,
      reactionBursts: state => state.roomState.reactionBursts,
      localParticipantName: state => state.preferences.displayName,
    }),
    ...mapWritableState(useVideoCallStore, ["isCallInFullscreen"]),
    ...mapState(useVideoCallStore, ["activeVideoCallChannel", "roomStateByEventId"]),
    ...mapState(useGeneralStore, ["webUrl", "userPhotoUrl", "username"]),
    ...mapState(useLivekitStore, {
      isHandRaised: state => state.local.handRaised,
      canPublishData: "canPublishData",
      roomModeration: state => state.roomState.moderation,
      hasUnmuteRequest: state => state.roomState.unmuteRequested,
      isWaitingForHostAccess: state => state.connection.waitingForHost,
    }),
    isWaitingForHost() {
      return this.callPhase === "waiting";
    },
    inviteLink() {
      return buildEventInviteLink(this.webUrl, this.eventId);
    },
    eventRoomState() {
      return this.roomStateByEventId[this.eventId] || { locked: false, waitingRoomEnabled: false, waiting: [] };
    },
    shouldPollRoomState() {
      return this.isRosterPanelVisible && this.canModerateRoom && Boolean(this.eventId);
    },
    ...mapWritableState(useVideoCallUiStore, [
      "viewPreferences",
      "focusedPeerId",
      "isAdjustViewPanelVisible",
      "isLocalVideoMirrored",
      "primaryViewPeerId",
      "isSettingsPanelVisible",
      "isRosterPanelVisible",
      "selectedCameraDeviceId",
      "selectedMicrophoneDeviceId",
      "selectedOutputDeviceId",
      "shouldShareSystemAudio",
      "isAutoJoinInProgress",
      "autoJoinErrorMessage",
      "isAutoJoinRefused",
      "isRequestingDevicePermission",
      "devicePromptErrorMessage",
      "shouldForceDevicePromptBanner",
      "mediaPermissionStates",
      "callPhase",
    ]),
    ...mapState(useVideoCallUiStore, {
      deniedMediaTypes: "deniedMediaTypes",
      deniedDeviceDisplayLabels: "deniedDeviceDisplayLabels",
      uiPermissionPrompt: "shouldDisplayDevicePermissionPrompt",
    }),
    ...mapState(useParticipantTilesStore, {
      participantTilesByPeerId: "tilesByPeerId",
      localCameraStream: "localCameraStream",
      localScreenShareStream: "localScreenShareStream",
      remoteParticipantTiles: "remoteTiles",
      participantTilesInGridOrder: "participantTilesInGridOrder",
      rosterEntries: "rosterEntries",
    }),
    callStateErrorMessage() {
      return this.livekitConnection?.error || this.deviceErrorNotice;
    },
    microphoneSilentNotice() {
      return MICROPHONE_SILENT_NOTICE;
    },
    showMicrophoneSilentNotice() {
      return this.isInCall && this.livekitConnection?.deviceError === MICROPHONE_SILENT_NOTICE;
    },
    deviceErrorNotice() {
      const deviceError = this.livekitConnection?.deviceError;
      if (!deviceError) {
        return "";
      }

      return deviceError === MICROPHONE_SILENT_NOTICE
        ? deviceError
        : "Your camera or microphone could not be used. Check the browser's permissions and try again.";
    },
    lobbyNoticeText() {
      return this.lobbyNotice || this.deviceErrorNotice;
    },
    lobbyJoinLabel() {
      if (this.isGuest) {
        return "Ask to join";
      }
      if (!this.isBroadcasting) {
        return "Join call";
      }

      return this.isOnBreak ? "Resume stream" : "Go live";
    },
    lobbyTitle() {
      if (!this.isBroadcasting) {
        return "Ready to join?";
      }

      return this.isOnBreak ? "Ready to come back?" : "Ready to go live?";
    },
    showCameraPausedNotice() {
      return this.isInCall && this.cameraPausedForAudio && !this.isCameraPausedNoticeDismissed && !this.callStateBannerVisible;
    },
    showAudioPlaybackBlockedNotice() {
      return this.isInCall && this.audioPlaybackBlocked && !this.callStateBannerVisible;
    },
    callStateBannerVisible() {
      return ["degraded", "error", "ended"].includes(this.callPhase);
    },
    paintsStage() {
      return !this.roomAppearance.hostPaintsSurface;
    },
    showTopLeftPill() {
      if (!this.showCallControlBar) {
        return false;
      }

      return this.isBroadcasting ? this.showBroadcastPill : this.showTitlePill && !this.titleSitsInControlBar;
    },
    titleSitsInControlBar() {
      return !this.isBroadcasting && !this.isWindowedCall;
    },
    showTitlePill() {
      return this.roomAppearance.showEventTitle || this.roomAppearance.showElapsedTimer;
    },
    showBroadcastPill() {
      return this.roomAppearance.showLiveBadge || this.roomAppearance.showElapsedTimer || this.roomAppearance.showViewerCount;
    },
    showCallControlBar() {
      return this.showCall && this.isInCall;
    },
    hostHasChat() {
      return Boolean(this.$.vnode.props?.onToggleChat);
    },
    hasChatPanel() {
      return Boolean(this.$slots["chat-panel"]);
    },
    requestedSidePanel() {
      if (this.showCallControlBar && this.isRosterPanelVisible) {
        return "people";
      }

      return this.isChatOpen && this.hasChatPanel ? "chat" : null;
    },
    sidePanelWidth() {
      return SIDE_PANEL_WIDTH;
    },
    peoplePanelMode() {
      return this.controlBarLayout === CONTROL_BAR_LAYOUT_ADAPTIVE ? "overlay" : "push";
    },
    showStageHint() {
      if (this.isBroadcasting) {
        return this.isInCall && !this.isJoiningOrWaiting && this.audienceCount === 0;
      }

      return this.showAloneHint;
    },
    showKnockNotice() {
      return this.canModerateRoom
        && !this.isBroadcasting
        && this.callPhase === "in-call"
        && this.waitingGuestCount > 0;
    },
    waitingGuestCount() {
      return Number(this.roomModeration?.waitingGuestCount || 0);
    },
    knockNoticeText() {
      return this.waitingGuestCount === 1 ? "Someone is asking to join" : `${this.waitingGuestCount} people are asking to join`;
    },
    stageHintText() {
      return this.isBroadcasting ? "No one is watching yet" : "You're the only one here";
    },
    audienceSummary() {
      return `${this.audienceCount} watching`;
    },
    namedRosterEntries() {
      return this.rosterEntries.map(entry => {
        if (!entry.isLocal) {
          return entry;
        }

        return { ...entry, displayName: this.localDisplayName };
      });
    },
    rosterEntriesForPanel() {
      if (!this.isBroadcasting) {
        return this.namedRosterEntries;
      }
      const viewers = this.remoteParticipantSummaries
          .filter(isViewerParticipant)
          .map(summary => ({
            id: summary.sid,
            identity: summary.identity,
            displayName: summary.name || "Viewer",
            isLocal: false,
            isViewer: true,
            isAudioMuted: false,
            isVideoMuted: false,
          }));

      return [...this.namedRosterEntries, ...viewers];
    },
    showAloneHint() {
      return this.isInCall && !this.isJoiningOrWaiting && !this.hasAnyRemoteStreams;
    },
    isGuest() {
      return Boolean(this.callRoom?.isGuest);
    },
    localDisplayName() {
      if (this.isGuest) {
        return this.guestName.trim() || "Guest";
      }

      return this.localParticipantName || this.username || "You";
    },
    prejoinAudioTrack() {
      return this.prejoinPreviewStream?.getAudioTracks?.()[0] || null;
    },
    stageLayoutMode() {
      return resolveStageLayout(this.viewPreferences.layout, {
        tileCount: this.stageTiles.length,
        hasScreenShare: this.stageTiles.some(tile => tile.isScreenShare),
        isFocused: Boolean(this.focusedPeerId),
      });
    },
    localSpeakerId() {
      return LOCAL_CAMERA_ID;
    },
    speakingPeerIds() {
      return this.rosterEntries
          .filter(entry => this.isParticipantSpeaking(entry.id))
          .map(entry => entry.id);
    },
    videoCallTitle() {
      if (this.callTitle) {
        return this.callTitle;
      }
      const channel = this.activeVideoCallChannel;
      if (!channel?.channel_name) {
        return "Video Call";
      }
      return `# ${channel.channel_name}`;
    },
    availableVideoDevices() {
      return this.videoInputs;
    },
    availableAudioInputDevices() {
      return this.audioInputs;
    },
    availableOutputDevices() {
      return this.audioOutputs;
    },
    rejoinStorage() {
      return callRejoinMarkers;
    },
    isWindowedCall() {
      return this.controlBarLayout === CONTROL_BAR_LAYOUT_ADAPTIVE;
    },
    rejoinMarkerId() {
      return this.eventId || this.callRoom?.rejoinMarkerId || 0;
    },
    isInCall() {
      return this.connected && this.callPhase === "in-call";
    },
    showPreJoinLobby() {
      return this.showCall && this.callPhase === "prejoin" && !this.isJoiningOrWaiting;
    },
    isLivekitConnecting() {
      return this.connecting;
    },
    isCameraEnabled() {
      return this.cameraOn;
    },
    isMicrophoneEnabled() {
      return this.micOn;
    },
    isScreenShareActive() {
      return this.sharing;
    },
    isScreenShareAudioActive() {
      return this.includeScreenAudio;
    },
    shouldDisplayDevicePermissionPrompt() {
      if (typeof this.uiPermissionPrompt !== "function") {
        return false;
      }
      return this.uiPermissionPrompt(this.showCall);
    },
    devicePromptHeadline() {
      if (this.isAutoJoinRefused) {
        return refusalBanner(this.autoJoinErrorMessage).headline;
      }
      if (this.autoJoinErrorMessage || this.devicePromptErrorMessage) {
        return "We couldn't start your call";
      }
      if (!this.deniedMediaTypes.length) {
        return "We can't access your devices";
      }
      if (this.deniedMediaTypes.length === 1) {
        const [device] = this.deniedMediaTypes;
        return `${device === "camera" ? "Camera" : "Microphone"} access is blocked`;
      }
      return "Camera and microphone access are blocked";
    },
    devicePromptMessage() {
      if (this.isAutoJoinRefused) {
        return refusalBanner(this.autoJoinErrorMessage).message;
      }
      if (this.devicePromptErrorMessage) {
        return this.devicePromptErrorMessage;
      }
      if (this.autoJoinErrorMessage) {
        return "Something went wrong while joining. Try again below.";
      }
      if (!this.deniedMediaTypes.length) {
        return "Grant access to your camera or microphone, then try again.";
      }
      if (this.deniedMediaTypes.length === 1) {
        const [device] = this.deniedMediaTypes;
        return `Your browser is blocking ${device} access. Allow it in your browser settings, then retry.`;
      }
      return "Your browser is blocking camera and microphone access. Allow them in your browser settings, then retry.";
    },
    emptyStateHeadline() {
      if (this.autoJoinErrorMessage) {
        return "We couldn't join the call";
      }
      if (this.shouldDisplayDevicePermissionPrompt) {
        return this.deniedDeviceDisplayLabels.length
          ? "Allow access to continue"
          : "Waiting for your camera or mic";
      }
      if (this.isInCall && !this.isJoiningOrWaiting) {
        return "You're in the call";
      }
      if (this.isWaitingForHost) {
        return "Waiting for the host to let you in";
      }
      if (this.isJoiningOrWaiting) {
        return "Joining your call";
      }
      return "Preparing your call";
    },
    emptyStateSupportingMessage() {
      if (this.autoJoinErrorMessage) {
        return "Retry below to reconnect.";
      }
      if (this.shouldDisplayDevicePermissionPrompt) {
        return this.deniedDeviceDisplayLabels.length
          ? "Enable access to your camera or microphone, then retry."
          : "We need your camera or microphone before others can see you.";
      }
      if (this.isInCall && !this.isJoiningOrWaiting) {
        return "Waiting for others to join.";
      }
      if (this.isWaitingForHost) {
        return "You'll join automatically as soon as they admit you.";
      }
      if (this.isJoiningOrWaiting) {
        return "Hang tight while we connect you.";
      }
      return "Setting up the meeting...";
    },
    hasAnyRemoteStreams() {
      return Object.keys(this.remoteParticipantTiles).length > 0 || !!this.localScreenShareStream;
    },
    isJoiningOrWaiting() {
      if (this.callPhase === "prejoin") {
        return false;
      }
      if (this.callPhase === "joining") {
        return true;
      }
      return this.isLivekitConnecting || this.isAutoJoinInProgress;
    },
    primaryTile() {
      return this.getTileEntryByPeerId(this.primaryViewPeerId);
    },
    localCameraTile() {
      return this.getTileEntryByPeerId(LOCAL_CAMERA_ID);
    },
    prejoinCameraTile() {
      if (this.prejoinPreviewStream?.getVideoTracks().length) {
        return {
          stream: this.prejoinPreviewStream,
          displayName: this.localDisplayName,
        };
      }
      return this.localCameraTile;
    },
    prejoinMicrophoneState() {
      return this.isInCall ? this.isMicrophoneEnabled : this.prejoinMicrophoneEnabled;
    },
    prejoinCameraState() {
      return this.isInCall ? this.isCameraEnabled : this.prejoinCameraEnabled;
    },
    stageTiles() {
      return this.participantTilesInGridOrder.map(tile => {
        if (!tile.isLocal || tile.isScreenShare) {
          return tile;
        }

        return { ...tile, displayName: this.localDisplayName };
      });
    },
    audioHostTiles() {
      if (!this.isInCall) {
        return [];
      }
      const remotes = this.remoteParticipantTiles || {};
      return Object.values(remotes).filter(tile => tile && tile.audioTrack);
    },
  },
  watch: {
    requestedSidePanel: {
      handler(next) {
        if (this.isSwappingToChat) {
          if (next === "chat") {
            this.isSwappingToChat = false;
            this.sidePanel = "chat";
          }

          return;
        }
        this.sidePanel = next;
      },
      immediate: true,
    },
    isChatOpen(isOpen) {
      if (isOpen && this.isRosterPanelVisible && !this.isSwappingToChat) {
        this.setRosterPanelVisible(false);
      }
    },
    sidePanel(panel, previous) {
      if (panel) {
        this.renderedSidePanel = panel;
      }
      if (Boolean(panel) !== Boolean(previous)) {
        this.anticipateStageResize(panel);
      }
    },
    cameraPausedForAudio(paused) {
      if (!paused) {
        this.isCameraPausedNoticeDismissed = false;
      }
    },
    connected(isConnected) {
      if (isConnected || this.isLeavingIntentionally) {
        return;
      }
      if (!["joining", "in-call", "degraded"].includes(this.callPhase)) {
        return;
      }
      this.handleUnexpectedDisconnect();
    },
    showCall: {
      immediate: true,
      handler(val) {
        if (val) {
          if (!this.isInCall) {
            this.resetCallPhase();
          }
          this.setCallPhase(this.isInCall ? "in-call" : "prejoin");
          this.resetAllScreenShareViewports();
          this.refreshAvailableDevices();
          this.refreshBrowserPermissionStatuses();
          this.prejoinMicrophoneEnabled = true;
          this.prejoinCameraEnabled = !this.audioOnlyPreference;
          this.stopPrejoinPreviewStream();
          if (this.resumeInterruptedCall()) {
            return;
          }
          this.maybeStartPrejoinPreviewStream();
        } else {
          this.resetCallStateAndDisconnect();
        }
      },
    },
    livekitRoom: {
      immediate: true,
      handler() {
        this.updateParticipantTilesFromLivekitRoom();
      },
    },
    remoteParticipantSummaries() {
      this.updateParticipantTilesFromLivekitRoom();
    },
    isScreenShareActive() {
      this.updateParticipantTilesFromLivekitRoom();
    },
    isCameraEnabled() {
      this.updateParticipantTilesFromLivekitRoom();
    },
    isInCall(connected) {
      if (connected && !this.isLeavingIntentionally) {
        this.rememberCallForReload();
      }
      if (connected) {
        this.micMeterStartTimer = window.setTimeout(() => this.startPublishedMicLevelMeter(), MIC_METER_ATTACH_DELAY_MS);

        return;
      }
      this.cancelPendingMicMeterStart();
      this.stopMicLevelMeter();
    },
    isScreenShareAudioActive(val) {
      this.setShouldShareSystemAudio(val);
    },
    shouldPollRoomState() {
      this.syncRoomStatePolling();
    },
    "roomModeration.waitingCount"() {
      if (this.shouldPollRoomState) {
        this.refreshEventRoomState();
      }
    },
    "roomModeration.waitingGuestCount"() {
      if (this.shouldPollRoomState) {
        this.refreshEventRoomState();
      }
    },
    availableVideoDevices() {
      this.ensureDeviceSelectionsAreValid();
    },
    availableAudioInputDevices() {
      this.ensureDeviceSelectionsAreValid();
    },
    availableOutputDevices() {
      this.ensureDeviceSelectionsAreValid();
    },
    selectedCameraDeviceId() {
      this.maybeStartPrejoinPreviewStream();
    },
    selectedMicrophoneDeviceId() {
      this.maybeStartPrejoinPreviewStream();
    },
    callPhase(val) {
      if (val === "in-call") {
        this.rememberCallForReload();
      }
      if (val === "prejoin") {
        this.maybeStartPrejoinPreviewStream();
      } else {
        this.stopPrejoinPreviewStream();
      }
    },
    prejoinCameraEnabled() {
      this.maybeStartPrejoinPreviewStream();
    },
    prejoinMicrophoneEnabled() {
      this.maybeStartPrejoinPreviewStream();
    },
    showPreJoinLobby: {
      immediate: true,
      handler(val) {
        if (val) {
          this.maybeStartPrejoinPreviewStream();
          this.prewarmAudioProcessing();
        } else {
          this.stopPrejoinPreviewStream();
        }
      },
    },
  },
  created() {
    this.syncingParticipants = false;
  },
  mounted() {
    this.permissionTracker = createPermissionTracker({
      onStateChange: this.updatePermissionState,
    });
    this.setShouldShareSystemAudio(this.isScreenShareAudioActive);
    window.addEventListener("keydown", this.handleCallShortcutDown);
    window.addEventListener("keyup", this.handleCallShortcutUp);
    this.restoreViewPreferences();
    this.ensureDeviceSelectionsAreValid();
    this.refreshAvailableDevices();
    this.watchDevices();
    this.updateParticipantTilesFromLivekitRoom();
    this.refreshBrowserPermissionStatuses();
  },
  beforeUnmount() {
    this.hasUnmounted = true;
    this.cancelPendingMicMeterStart();
    this.stopWaitingPoll();
    if (this.rosterPollTimer) {
      window.clearInterval(this.rosterPollTimer);
    }
    window.removeEventListener("keydown", this.handleCallShortcutDown);
    window.removeEventListener("keyup", this.handleCallShortcutUp);
    this.unwatchDevices();
    if (this.permissionTracker) {
      this.permissionTracker.dispose();
      this.permissionTracker = null;
    }
    this.stopPrejoinPreviewStream();
    this.resetParticipantTiles();
  },
  methods: {
    ...mapActions(useLivekitStore, {
      keepCameraOnDespiteWeakUplink: "keepCameraOnDespiteWeakUplink",
      startLivekitCallInRoom: "startCallInRoom",
      applyAudioOutputDevice: "applyAudioOutputDevice",
      resumeAudioPlayback: "resumeAudioPlayback",
      endLivekitCall: "endCall",
      toggleLivekitMic: "toggleMic",
      setLivekitMicEnabled: "setMicEnabled",
      toggleLivekitCamera: "toggleCamera",
      toggleLivekitScreenShare: "toggleScreenShare",
      refreshAvailableDevices: "refreshDevices",
      watchDevices: "watchDevices",
      unwatchDevices: "unwatchDevices",
      setPreferredMicDeviceId: "setPreferredMicDevice",
      setPreferredCameraDeviceId: "setPreferredCameraDevice",
      isParticipantSpeakingByPeerIdFromStore: "isParticipantSpeakingByPeerId",
      applyVideoSubscriptionPlan: "applyVideoSubscriptionPlan",
      muteParticipantForEveryone: "muteParticipantForEveryone",
      endEventForEveryone: "endEventForEveryone",
      removeParticipantForEveryone: "removeParticipantForEveryone",
      requestParticipantUnmute: "requestParticipantUnmute",
      updateEventRoomState: "updateEventRoomState",
      admitWaitingUser: "admitWaitingUser",
      denyWaitingUser: "denyWaitingUser",
      admitWaitingGuest: "admitWaitingGuest",
      denyWaitingGuest: "denyWaitingGuest",
      checkGuestAdmission: "checkGuestAdmission",
      leaveGuestWaitingRoom: "leaveGuestWaitingRoom",
      dismissUnmuteRequest: "dismissUnmuteRequest",
      toggleHand: "toggleHand",
      sendReaction: "sendReaction",
      lowerParticipantHand: "lowerParticipantHand",
      setNoiseCancellationMode: "setNoiseCancellationMode",
      setBroadcastStatus: "setBroadcastStatus",
      prewarmAudioProcessing: "prewarmAudioProcessing",
      recordMicTestSample: "recordMicTestSample",
      playMicTestSample: "playMicTestSample",
    }),
    ...mapActions(useVideoCallUiStore, [
      "setViewPreferences",
      "restoreViewPreferences",
      "focusPeer",
      "clearFocus",
      "setAdjustViewPanelVisible",
      "setPrimaryViewPeerId",
      "setSettingsPanelVisible",
      "setRosterPanelVisible",
      "setMediaPermissionState",
      "setDeviceSelections",
      "setAutoJoinState",
      "setDevicePromptError",
      "setRequestingDevicePermission",
      "setShouldForceDevicePromptBanner",
      "setShouldShareSystemAudio",
      "setLocalVideoMirrored",
      "resetAllScreenShareViewports",
      "setCallPhase",
      "resetCallPhase",
    ]),
    ...mapActions(useVideoCallStore, ["fetchEventRoomState"]),
    ...mapActions(useParticipantTilesStore, {
      syncParticipantTilesFromRoom: "syncFromLivekitRoom",
      resetParticipantTiles: "resetTiles",
    }),
    rememberCallForReload() {
      if (!this.rejoinMarkerId) {
        return;
      }
      rememberInCall(this.rejoinStorage, this.rejoinMarkerId, {
        micEnabled: this.prejoinMicrophoneEnabled,
        cameraEnabled: this.prejoinCameraEnabled,
      });
    },
    resumeInterruptedCall() {
      const marker = readRejoin(this.rejoinStorage, this.rejoinMarkerId);
      if (!marker || this.isInCall) {
        return false;
      }
      this.prejoinMicrophoneEnabled = marker.micEnabled;
      this.prejoinCameraEnabled = marker.cameraEnabled;
      this.lobbyNotice = "";
      this.attemptAutoJoinCall({
        micEnabled: marker.micEnabled,
        cameraEnabled: marker.cameraEnabled,
        usePreferences: true,
      });

      return true;
    },
    joinCallWithPrejoinState({ audioOnly = false } = {}) {
      const livekitStore = useLivekitStore();
      if (this.isGuest) {
        livekitStore.setGuestDisplayName(this.guestName);
      }
      if (audioOnly && this.prejoinCameraEnabled) {
        this.prejoinCameraEnabled = false;
      }
      const shouldEnableCamera = audioOnly ? false : this.prejoinCameraEnabled;
      const mediaOptions = {
        micEnabled: this.prejoinMicrophoneEnabled,
        cameraEnabled: shouldEnableCamera,
        usePreferences: true,
      };
      if (livekitStore?.preferences) {
        livekitStore.preferences.audioOnly = !shouldEnableCamera;
      }
      this.stopPrejoinPreviewStream();
      this.lobbyNotice = "";
      return this.attemptAutoJoinCall(mediaOptions);
    },
    async attemptAutoJoinCall(mediaOptions = {}) {
      if (!this.callRoom) {
        return;
      }
      if (this.isInCall) {
        this.setCallPhase("in-call");
        this.updateParticipantTilesFromLivekitRoom();
        return;
      }
      if (this.isLivekitConnecting) {
        return;
      }
      this.setCallPhase("joining");
      this.setAutoJoinState({
        inProgress: true,
        errorMessage: null,
        refused: false,
      });
      this.lastJoinMediaOptions = mediaOptions;
      try {
        await this.startLivekitCallInRoom(this.callRoom, {
          ...mediaOptions,
          broadcast: this.isBroadcasting,
        });
        if (this.isWaitingForHostAccess) {
          this.enterWaitingRoom();
          return;
        }
        if (this.callPhase !== "joining" || !this.connected) {
          return;
        }
        this.setShouldForceDevicePromptBanner(false);
        this.setCallPhase("in-call");
        this.markBroadcastLive();
      } catch (error) {
        if (this.callPhase !== "joining") {
          return;
        }
        this.setAutoJoinState({
          errorMessage: error?.message || "Unable to join the call automatically.",
          refused: isJoinRefusal(error),
        });
        this.setShouldForceDevicePromptBanner(true);
        this.setCallPhase("prejoin");
      } finally {
        this.setAutoJoinState({ inProgress: false });
        this.updateParticipantTilesFromLivekitRoom();
        this.refreshBrowserPermissionStatuses();
      }
    },
    enterWaitingRoom() {
      this.setCallPhase("waiting");
      this.stopWaitingPoll();
      this.waitingPollTimer = window.setInterval(this.checkWaitingRoomAdmission, WAITING_ROOM_POLL_MS);
    },
    checkWaitingRoomAdmission() {
      if (this.isGuest) {
        this.checkGuestAdmissionOnce();
        return;
      }
      this.fetchEventRoomState(this.eventId, roomState => {
        if (!roomState || !this.isWaitingForHost) {
          return;
        }
        if (roomState.admission === ADMISSION_ADMITTED) {
          this.stopWaitingPoll();
          this.attemptAutoJoinCall(this.lastJoinMediaOptions || {});
          return;
        }
        if (roomState.admission !== ADMISSION_WAITING) {
          this.leaveWaitingRoom(roomState.admission === ADMISSION_LOCKED
            ? "The host has locked this meeting."
            : "The host didn't let you into this event.");
        }
      });
    },
    checkGuestAdmissionOnce() {
      this.checkGuestAdmission(this.callRoom, access => {
        if (!this.isWaitingForHost || access.waiting) {
          return;
        }
        this.stopWaitingPoll();
        this.attemptAutoJoinCall(this.lastJoinMediaOptions || {});
      }, error => {
        if (error?.status === HTTP_STATUS_LOCKED) {
          this.leaveWaitingRoom("The host has locked this meeting.");
          return;
        }
        if (error?.status === HTTP_STATUS_FORBIDDEN) {
          this.leaveWaitingRoom("The host didn't let you into this event.");
          return;
        }
        // eslint-disable-next-line no-console
        console.error("[Lobby] Could not check whether the host let the guest in; still waiting", { eventId: this.eventId, error });
      });
    },
    stopWaiting() {
      if (this.isGuest) {
        this.leaveGuestWaitingRoom(this.callRoom);
      }
      this.leaveWaitingRoom();
    },
    signInInstead() {
      window.location.assign(signInUrlForEvent(this.webUrl, window.location.href));
    },
    leaveWaitingRoom(notice = "") {
      this.stopWaitingPoll();
      this.lobbyNotice = notice;
      this.setCallPhase("prejoin");
    },
    stopWaitingPoll() {
      if (this.waitingPollTimer) {
        window.clearInterval(this.waitingPollTimer);
        this.waitingPollTimer = null;
      }
    },
    refreshEventRoomState() {
      if (this.eventId) {
        this.fetchEventRoomState(this.eventId, () => {});
      }
    },
    syncRoomStatePolling() {
      if (this.rosterPollTimer) {
        window.clearInterval(this.rosterPollTimer);
        this.rosterPollTimer = null;
      }
      if (!this.shouldPollRoomState) {
        return;
      }
      this.refreshEventRoomState();
      this.rosterPollTimer = window.setInterval(this.refreshEventRoomState, WAITING_ROOM_POLL_MS);
    },
    async acceptUnmuteRequest() {
      this.dismissUnmuteRequest();
      await this.setLivekitMicEnabled(true);
      if (this.micOn) {
        this.updatePermissionState("microphone", "granted");
      }
    },
    askToRemove({ identity, displayName }) {
      this.pendingRemoval = { identity, displayName };
    },
    confirmRemoveParticipant() {
      const removal = this.pendingRemoval;
      this.pendingRemoval = null;
      if (removal && this.eventId) {
        this.removeParticipantForEveryone(this.eventId, removal.identity);
      }
    },
    lowerHand({ identity }) {
      if (this.eventId) {
        this.lowerParticipantHand(this.eventId, identity);
      }
    },
    requestUnmute({ identity }) {
      if (this.eventId) {
        this.requestParticipantUnmute(this.eventId, identity);
      }
    },
    updateRoomState({ locked, waitingRoomEnabled }) {
      if (this.eventId) {
        this.updateEventRoomState(this.eventId, { locked, waitingRoomEnabled }, this.refreshEventRoomState);
      }
    },
    admitUser(userId) {
      if (this.eventId) {
        this.admitWaitingUser(this.eventId, userId, this.refreshEventRoomState);
      }
    },
    denyUser(userId) {
      if (this.eventId) {
        this.denyWaitingUser(this.eventId, userId, this.refreshEventRoomState);
      }
    },
    admitGuest(guestId) {
      if (this.eventId) {
        this.admitWaitingGuest(this.eventId, guestId, this.refreshEventRoomState);
      }
    },
    denyGuest(guestId) {
      if (this.eventId) {
        this.denyWaitingGuest(this.eventId, guestId, this.refreshEventRoomState);
      }
    },
    async resetCallStateAndDisconnect() {
      this.stopWaitingPoll();
      this.setAutoJoinState({ inProgress: false, errorMessage: null });
      this.setShouldForceDevicePromptBanner(false);
      this.resetParticipantTiles();
      this.setPrimaryViewPeer(LOCAL_CAMERA_ID, { forceSync: true });
      this.resetAllScreenShareViewports();
      this.stopPrejoinPreviewStream();
      this.prejoinMicrophoneEnabled = true;
      this.prejoinCameraEnabled = !this.audioOnlyPreference;
      this.resetCallPhase();
      this.isLeavingIntentionally = true;
      try {
        await this.endLivekitCall();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[VideoCall] leaving the call failed to disconnect cleanly; the room may still count you", error);
      } finally {
        this.isLeavingIntentionally = false;
      }
    },
    handleLeaveRequest() {
      if (this.isBroadcasting && this.canModerateRoom && this.eventId) {
        this.isEndStreamConfirmVisible = true;

        return;
      }
      this.leaveCallFromControlBar();
    },
    confirmEndStream() {
      this.isEndStreamConfirmVisible = false;
      this.isOnBreak = false;
      this.endForEveryone();
    },
    async pauseBroadcast() {
      if (!this.eventId) {
        return;
      }
      this.isOnBreak = true;
      this.isLeavingIntentionally = true;
      forgetInCall(this.rejoinStorage, this.eventId);
      await this.setBroadcastStatus(this.eventId, "paused");
      try {
        await this.endLivekitCall();
      } finally {
        this.stopPrejoinPreviewStream();
        this.resetParticipantTiles();
        this.setPrimaryViewPeer(LOCAL_CAMERA_ID, { forceSync: true });
        this.resetAllScreenShareViewports();
        this.resetCallPhase();
        this.isLeavingIntentionally = false;
        this.setCallPhase("prejoin");
        this.lobbyNotice = "You're on a break. Viewers see your break screen and hear nothing until you resume.";
        this.maybeStartPrejoinPreviewStream();
      }
    },
    resumeBroadcast() {
      return this.joinCallWithPrejoinState();
    },
    async markBroadcastLive() {
      if (!this.isBroadcasting || !this.eventId) {
        return;
      }
      this.isOnBreak = false;
      await this.setBroadcastStatus(this.eventId, "live");
    },
    async leaveCallFromControlBar() {
      this.isLeavingIntentionally = true;
      forgetInCall(this.rejoinStorage, this.rejoinMarkerId);
      try {
        await this.endLivekitCall();
      } finally {
        this.tearDownAfterLeaving();
      }
    },
    tearDownAfterLeaving() {
      this.stopPrejoinPreviewStream();
      this.resetParticipantTiles();
      this.setPrimaryViewPeer(LOCAL_CAMERA_ID, { forceSync: true });
      this.resetAllScreenShareViewports();
      this.resetCallPhase();
      this.isLeavingIntentionally = false;
      this.$emit("close");
    },
    handleUnexpectedDisconnect() {
      const notices = {
        "removed": "The host removed you from this call.",
        "duplicate": "You joined this call from another tab or device, so this one was disconnected.",
      };
      // eslint-disable-next-line no-console
      console.info("[VideoCall] the room dropped the connection — back to the lobby", { reason: this.disconnectReason });
      this.resetParticipantTiles();
      this.setPrimaryViewPeer(LOCAL_CAMERA_ID, { forceSync: true });
      this.resetAllScreenShareViewports();
      this.resetCallPhase();
      if (this.disconnectReason === "room-ended" || this.disconnectReason === "removed") {
        forgetInCall(this.rejoinStorage, this.rejoinMarkerId);
      }
      if (this.disconnectReason === "room-ended" && this.eventId) {
        this.$emit("close");
        return;
      }
      this.setCallPhase("prejoin");
      this.lobbyNotice = notices[this.disconnectReason] || "Your connection to the call dropped. Check your internet connection and join again.";
    },
    retryAutoJoinRequest() {
      this.joinCallWithPrejoinState();
    },
    async requestDeviceAccessForCall() {
      if (this.isRequestingDevicePermission) {
        return;
      }
      this.setDevicePromptError(null);
      this.setRequestingDevicePermission(true);
      this.setShouldForceDevicePromptBanner(true);
      try {
        if (!this.isInCall && !this.isLivekitConnecting) {
          await this.attemptAutoJoinCall();
        }
        if (!this.isInCall) {
          throw new Error("Still connecting... please try again in a moment.");
        }
        if (!this.isMicrophoneEnabled) {
          await this.toggleLivekitMic();
        }
        if (!this.isCameraEnabled) {
          await this.toggleLivekitCamera();
        }
        if (!this.isMicrophoneEnabled && !this.isCameraEnabled) {
          this.setDevicePromptError("Camera and microphone are unavailable on this device.");
        }
      } catch (error) {
        const deniedDevices = inferDeniedDevicesFromErrorUtil(error);
        deniedDevices.forEach(device => this.updatePermissionState(device, "denied"));
        if (!this.devicePromptErrorMessage) {
          this.setDevicePromptError(error?.message || "Unable to enable camera or microphone.");
        }
      } finally {
        this.setRequestingDevicePermission(false);
        this.refreshBrowserPermissionStatuses();
      }
    },
    refreshBrowserPermissionStatuses() {
      this.permissionTracker?.refresh();
    },
    updatePermissionState(key, state) {
      if (!key || !state) {
        return;
      }
      this.setMediaPermissionState(key, state);
    },
    async maybeStartPrejoinPreviewStream() {
      const wantsVideo = this.showPreJoinLobby && this.prejoinCameraEnabled;
      const wantsAudio = this.showPreJoinLobby && this.prejoinMicrophoneEnabled;
      if (!wantsVideo && !wantsAudio) {
        this.stopPrejoinPreviewStream();
        return;
      }
      if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
        return;
      }
      const current = this.prejoinPreviewStream;
      const previousAudio = current?.getAudioTracks?.()[0] || null;
      const plan = planPreviewTracks({
        wantsVideo,
        wantsAudio,
        videoTrack: current?.getVideoTracks?.()[0] || null,
        audioTrack: previousAudio,
        videoDeviceId: this.selectedCameraDeviceId,
        audioDeviceId: this.selectedMicrophoneDeviceId,
      });
      this.previewRequestToken += 1;
      const requestToken = this.previewRequestToken;
      plan.stop.forEach(track => track.stop());

      let requested = [];
      if (plan.requestVideo || plan.requestAudio) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: plan.requestVideo ? this.prejoinVideoConstraints() : false,
            audio: plan.requestAudio ? useLivekitStore().microphoneConstraints(this.selectedMicrophoneDeviceId) : false,
          });
          requested = stream.getTracks();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("[Lobby] The browser would not start the preview", {
            camera: plan.requestVideo,
            microphone: plan.requestAudio,
            error,
          });
        }
      }
      if (requestToken !== this.previewRequestToken || !this.showPreJoinLobby || this.hasUnmounted) {
        requested.forEach(track => track.stop());
        return;
      }
      const tracks = [...plan.keep, ...requested];
      this.prejoinPreviewStream = tracks.length ? new MediaStream(tracks) : null;
      const audio = tracks.find(track => track.kind === "audio") || null;
      if (!audio) {
        this.stopMicLevelMeter();
      } else if (audio !== previousAudio || !this.micLevelMeterHandle) {
        this.startMicLevelMeter(new MediaStream([audio]));
      }
      if (requested.length) {
        this.refreshAvailableDevices();
      }
    },
    prejoinVideoConstraints() {
      return this.selectedCameraDeviceId ? { deviceId: this.selectedCameraDeviceId } : true;
    },
    startMicLevelMeter(stream) {
      this.stopMicLevelMeter();
      this.micLevelMeterHandle = createMicLevelMeter(stream, useLivekitStore()._ensureAudioContext(), level => {
        this.micLevel = level;
      });
    },
    stopMicLevelMeter() {
      if (this.micLevelMeterHandle) {
        this.micLevelMeterHandle.stop();
        this.micLevelMeterHandle = null;
      }
      this.micLevel = 0;
    },
    cancelPendingMicMeterStart() {
      if (this.micMeterStartTimer) {
        window.clearTimeout(this.micMeterStartTimer);
        this.micMeterStartTimer = null;
      }
    },
    startPublishedMicLevelMeter() {
      const track = useLivekitStore().publishedMicrophoneTrack();
      if (!track || typeof MediaStream === "undefined") {
        return;
      }
      this.startMicLevelMeter(new MediaStream([track]));
    },
    stopPrejoinPreviewStream() {
      this.previewRequestToken += 1;
      this.stopMicLevelMeter();
      if (this.prejoinPreviewStream?.getTracks) {
        this.prejoinPreviewStream.getTracks().forEach(track => track.stop());
      }
      this.prejoinPreviewStream = null;
    },
    handlePreJoinToggleMic() {
      if (this.showPreJoinLobby) {
        this.prejoinMicrophoneEnabled = !this.prejoinMicrophoneEnabled;
        return;
      }
      this.toggleMicrophoneAndUpdatePermissions();
    },
    handlePreJoinToggleCamera() {
      if (this.showPreJoinLobby) {
        this.prejoinCameraEnabled = !this.prejoinCameraEnabled;
        return;
      }
      this.toggleCameraAndUpdatePermissions();
    },
    async handlePreJoinCameraChange(cameraId) {
      const targetId = cameraId || null;
      this.setDeviceSelections({ cameraId: targetId });
      await this.setPreferredCameraDeviceId(targetId);
      if (this.isInCall) {
        await this.applySelectedCameraDevice(targetId);
      }
      if (this.showPreJoinLobby) {
        this.maybeStartPrejoinPreviewStream();
      }
    },
    async handlePreJoinMicrophoneChange(micId) {
      const targetId = micId || null;
      this.setDeviceSelections({ micId: targetId });
      await this.setPreferredMicDeviceId(targetId);
      if (this.isInCall) {
        await this.applySelectedMicrophoneDevice(targetId);
      }
    },
    toggleSettingsPanel() {
      this.setSettingsPanelVisible(!this.isSettingsPanelVisible);
      if (this.isSettingsPanelVisible) {
        this.refreshAvailableDevices();
      }
    },
    closeSidePanels() {
      this.setRosterPanelVisible(false);
      this.setSettingsPanelVisible(false);
    },
    toggleChatSidebar() {
      if (!this.isChatOpen && this.isRosterPanelVisible && this.hasChatPanel) {
        this.isSwappingToChat = true;
        this.setRosterPanelVisible(false);
      }
      this.$emit("toggle-chat");
    },
    async toggleMicrophoneAndUpdatePermissions() {
      await this.toggleLivekitMic();
      if (this.micOn) {
        this.updatePermissionState("microphone", "granted");
      }
    },
    async toggleCameraAndUpdatePermissions() {
      await this.toggleLivekitCamera();
      if (this.cameraOn) {
        this.updatePermissionState("camera", "granted");
      }
    },
    toggleLocalMirroringPreference(value) {
      if (typeof value === "boolean") {
        this.setLocalVideoMirrored(value);
        return;
      }
      this.setLocalVideoMirrored(!this.isLocalVideoMirrored);
    },
    async applySelectedCameraDevice(cameraId = this.selectedCameraDeviceId) {
      if (!cameraId) {
        return;
      }
      this.setDeviceSelections({ cameraId });
      await this.setPreferredCameraDeviceId(cameraId);
      if (!this.isCameraEnabled) {
        await this.toggleLivekitCamera();
      }
    },
    async applySelectedMicrophoneDevice(micId = this.selectedMicrophoneDeviceId) {
      if (!micId) {
        return;
      }
      this.setDeviceSelections({ micId });
      await this.setPreferredMicDeviceId(micId);
      if (!this.isMicrophoneEnabled) {
        await this.toggleLivekitMic();
      }
    },
    commitOutputDeviceSelection(outputId = this.selectedOutputDeviceId) {
      this.setDeviceSelections({ outputId });
      this.applyAudioOutputDevice(outputId);
    },
    ensureDeviceSelectionsAreValid() {
      this.setDeviceSelections({
        cameraId: pickValidDeviceId(this.selectedCameraDeviceId, this.preferredCamera, this.availableVideoDevices),
        micId: pickValidDeviceId(this.selectedMicrophoneDeviceId, this.preferredMic, this.availableAudioInputDevices),
        outputId: pickValidDeviceId(this.selectedOutputDeviceId, null, this.availableOutputDevices),
      });
    },
    isParticipantSpeaking(peerId) {
      return this.isParticipantSpeakingByPeerIdFromStore(peerId);
    },
    handleCallShortcutDown(event) {
      if (!this.connected || isTypingTarget(event.target)) {
        return;
      }
      const shortcut = matchCallShortcut(event);
      if (shortcut === CALL_SHORTCUTS.toggleMicrophone) {
        event.preventDefault();
        this.toggleMicrophoneAndUpdatePermissions();

        return;
      }
      if (shortcut === CALL_SHORTCUTS.toggleCamera) {
        event.preventDefault();
        this.toggleCameraAndUpdatePermissions();

        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      if (event.code === "Space") {
        if (!this.isPushToTalkActive && !this.isMicrophoneEnabled) {
          this.isPushToTalkActive = true;
          event.preventDefault();
          this.toggleLivekitMic();
        }

        return;
      }
      if (event.key === "m" || event.key === "M") {
        event.preventDefault();
        this.toggleLivekitMic();

        return;
      }
      if (event.key === "v" || event.key === "V") {
        event.preventDefault();
        this.toggleLivekitCamera();
      }
    },
    handleCallShortcutUp(event) {
      if (event.code !== "Space" || !this.isPushToTalkActive) {
        return;
      }
      this.isPushToTalkActive = false;
      this.toggleLivekitMic();
    },
    copyInviteLinkFromNotice() {
      if (!this.inviteLink) {
        throw new Error("The alone notice offered to copy an invite link, but this call has none");
      }
      navigator.clipboard.writeText(this.inviteLink).then(() => {
        this.isInviteLinkCopied = true;
        window.clearTimeout(this.inviteCopiedTimer);
        this.inviteCopiedTimer = window.setTimeout(() => {
          this.isInviteLinkCopied = false;
        }, INVITE_COPIED_MS);
      }).catch(error => {
        // eslint-disable-next-line no-console
        console.error("[video-call] Copying the invite link from the alone notice failed", { inviteLink: this.inviteLink, error });
      });
    },
    anticipateStageResize(openingPanel) {
      const mode = (openingPanel || this.renderedSidePanel) === "chat" ? this.chatPanelMode : this.peoplePanelMode;
      if (mode !== "push") {
        return;
      }
      const openingWidth = this.sidePanelWidth;
      const closingWidth = this.$refs.sideReveal?.$el?.offsetWidth || 0;
      const delta = openingPanel ? -openingWidth : closingWidth;
      window.clearTimeout(this.stageSettleTimer);
      this.$nextTick(() => window.requestAnimationFrame(() => {
        this.stageSettleDelta = delta;
        this.stageSettleTimer = window.setTimeout(() => {
          this.stageSettleDelta = null;
        }, STAGE_SETTLE_MS);
      }));
    },
    showParticipants() {
      if (!this.isRosterPanelVisible) {
        this.toggleRosterPanel();
      }
    },
    toggleRosterPanel() {
      const isOpening = !this.isRosterPanelVisible;
      this.setRosterPanelVisible(isOpening);
      if (isOpening && this.isChatOpen) {
        this.$emit("toggle-chat");
      }
    },
    muteParticipant({ identity, trackSid } = {}) {
      if (!this.eventId || !identity) {
        return;
      }
      this.muteParticipantForEveryone(this.eventId, identity, trackSid);
    },
    endForEveryone() {
      if (!this.eventId) {
        return;
      }
      this.isLeavingIntentionally = true;
      this.endEventForEveryone(this.eventId, () => {
        this.tearDownAfterLeaving();
      }, () => {
        this.tearDownAfterLeaving();
      });
    },
    focusGridTileInSpeakerView(participant) {
      if (!participant?.id) {
        return;
      }
      this.focusParticipantInSpeakerView(participant.id, { requestKeyFrame: true });
      this.focusPeer(participant.id);
    },
    applyViewPreferences(patch) {
      this.setViewPreferences(patch);
      if (patch.layout) {
        this.restorePreviousSpeakerView();
      }
    },
    restorePreviousSpeakerView() {
      if (!this.focusedPeerId) {
        return;
      }
      this.clearFocus();
      this.primaryChosenByUser = false;
      this.ensurePrimaryViewIsValid();
    },
    focusParticipantInSpeakerView(peerId, { requestKeyFrame = false } = {}) {
      const tile = this.getTileEntryByPeerId(peerId);
      if (!tile) {
        return;
      }
      if (requestKeyFrame && typeof tile.videoTrack?.requestKeyFrame === "function") {
        tile.videoTrack.requestKeyFrame();
      }
      this.primaryChosenByUser = true;
      this.setPrimaryViewPeer(tile.id, { forceSync: true });
    },
    setPrimaryViewPeer(peerId, options = {}) {
      const targetPeerId = peerId || LOCAL_CAMERA_ID;
      if (!options.forceSync && this.primaryViewPeerId === targetPeerId) {
        return;
      }
      this.setPrimaryViewPeerId(targetPeerId);
    },
    ensurePrimaryViewIsValid() {
      const tiles = Object.values(this.participantTilesByPeerId).filter(Boolean);
      if (this.focusedPeerId && !tiles.some(tile => tile.id === this.focusedPeerId)) {
        this.clearFocus();
        this.primaryChosenByUser = false;
      }
      const next = pickPrimaryPeerId({
        currentPeerId: this.primaryViewPeerId,
        chosenByUser: this.primaryChosenByUser,
        tiles,
      });
      const resolved = next || LOCAL_CAMERA_ID;
      if (resolved !== this.primaryViewPeerId) {
        this.setPrimaryViewPeer(resolved);
      }
    },
    getTileEntryByPeerId(peerId) {
      if (peerId === LOCAL_CAMERA_ID) {
        return this.participantTilesByPeerId[LOCAL_CAMERA_ID] || null;
      }
      if (peerId === LOCAL_SHARE_ID) {
        return this.participantTilesByPeerId[LOCAL_SHARE_ID] || null;
      }
      return this.participantTilesByPeerId[peerId] || null;
    },
    updateParticipantTilesFromLivekitRoom() {
      if (this.syncingParticipants) {
        return;
      }
      this.syncingParticipants = true;
      try {
        this.syncParticipantTilesFromRoom();
        this.ensurePrimaryViewIsValid();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[LiveKit] Failed to sync participant tiles; the roster may be stale", {
          primaryViewPeerId: this.primaryViewPeerId,
          tileCount: Object.keys(this.participantTilesByPeerId || {}).length,
          error,
        });
      } finally {
        this.syncingParticipants = false;
      }
    },
    handleScreenShareButtonClick() {
      if (!this.isInCall) {
        return;
      }
      if (this.isScreenShareActive) {
        this.stopPresenting();
        return;
      }
      this.toggleLivekitScreenShare({ includeAudio: true });
    },
    stopPresenting() {
      if (!this.isScreenShareActive) {
        return;
      }
      this.toggleLivekitScreenShare();
    },
    emitPopOutRequest() {
      if (this.isCallInFullscreen) {
        this.exitCallFullscreen();
      }
      this.$emit("pop-out");
    },
    emitPopInRequest() {
      if (this.isCallInFullscreen) {
        this.exitCallFullscreen();
      }
      this.$emit("pop-in");
    },
    exitCallFullscreen() {
      exitFullscreen();
      this.isCallInFullscreen = false;
    },
    enterCallFullscreen() {
      const container = this.$refs.videoCallContainer;
      if (!container) {
        return;
      }
      enterFullscreen(container);
      this.isCallInFullscreen = true;
    },
  },
  styleGuide: () => ({
    callBackground: { "color.background": "paper" },
    callBorderRadius: { "size.borderRadius": "large" },
    stageBackground: { "color.background": "paper" },
  }),
};
</script>

<style scoped>
.modal-body {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: transparent;
  display: flex;
  flex-direction: column;
}

.call-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
}

.rounded {
  border-radius: v-bind("$getStyles.callBorderRadius");
  background: v-bind("$getStyles.callBackground");
}

.modal-body.paints-stage,
.video-stage.paints-stage {
  background: v-bind("$getStyles.stageBackground");
}

.video-stage {
  width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.audio-host {
  display: none;
}
</style>
